import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaMapMarkerAlt, FaTags, FaDollarSign, FaClock, FaShieldAlt, FaRedo, FaCheck } from 'react-icons/fa';
import './filters.css';

const ServiceProviderFilter = ({ toggleFilter, currentFilters = {}, onApply, onClear, categories = [] }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const [location, setLocation] = useState(currentFilters.location || '');
  const [category, setCategory] = useState(currentFilters.category || '');
  const [minPrice, setMinPrice] = useState(currentFilters.minPrice || '');
  const [maxPrice, setMaxPrice] = useState(currentFilters.maxPrice || '');
  const [availability, setAvailability] = useState(currentFilters.availability || 'now');

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(toggleFilter, 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const filters = {};
    if (location.trim()) filters.location = location.trim();
    if (category) filters.category = category;
    if (minPrice !== '') filters.minPrice = minPrice;
    if (maxPrice !== '') filters.maxPrice = maxPrice;
    if (availability) filters.availability = availability;
    if (onApply) onApply(filters);
    handleClose();
  };

  const handleReset = () => {
    setLocation('');
    setCategory('');
    setMinPrice('');
    setMaxPrice('');
    setAvailability('now');
    if (onClear) onClear();
  };

  const activeCount = [location, category, minPrice, maxPrice].filter(v => v).length + (availability ? 1 : 0);

  // Lock body scroll while filter is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const filterUI = (
    <div
      className={`sf-filter-overlay ${isVisible ? 'sf-filter-overlay--visible' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`sf-filter-panel ${isVisible ? 'sf-filter-panel--open' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="sf-filter-header">
          <div className="sf-filter-header-left">
            <h2>{t('service_filter.title')}</h2>
            {activeCount > 0 && <span className="sf-filter-badge">{activeCount}</span>}
          </div>
          <button className="sf-filter-close" onClick={handleClose} aria-label="Close filters">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="sf-filter-body">
          <div className="sf-filter-section">
            <h3><FaMapMarkerAlt /> {t('service_filter.location')}</h3>
            <div className="sf-filter-input-wrap">
              <input type="text" placeholder={t('service_filter.location_placeholder')} value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
          </div>

          <div className="sf-filter-section">
            <h3><FaTags /> {t('service_filter.service_category')}</h3>
            {categories.length > 0 ? (
              <div className="sf-filter-chips">
                {categories.map((cat) => (
                  <button key={cat} type="button" className={`sf-filter-chip ${category === cat ? 'sf-filter-chip--active' : ''}`} onClick={() => setCategory(category === cat ? '' : cat)}>
                    {category === cat && <FaCheck />} {cat}
                  </button>
                ))}
              </div>
            ) : (
              <p className="sf-filter-empty">{t('service_filter.no_categories', 'No categories available')}</p>
            )}
          </div>

          <div className="sf-filter-section">
            <h3><FaDollarSign /> {t('service_filter.price_range')}</h3>
            <div className="sf-filter-price-row">
              <div className="sf-filter-input-wrap">
                <input type="number" min="0" placeholder={t('service_filter.min_price')} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
              </div>
              <span className="sf-filter-price-sep">—</span>
              <div className="sf-filter-input-wrap">
                <input type="number" min="0" placeholder={t('service_filter.max_price')} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="sf-filter-section">
            <h3><FaClock /> {t('service_filter.availability')}</h3>
            <div className="sf-filter-chips">
              {[{ value: 'now', label: t('service_filter.available_now') }, { value: 'weekdays', label: t('service_filter.weekdays') }, { value: 'weekends', label: t('service_filter.weekends') }].map(({ value, label }) => (
                <button key={value} type="button" className={`sf-filter-chip ${availability === value ? 'sf-filter-chip--active' : ''}`} onClick={() => setAvailability(availability === value ? '' : value)}>
                  {availability === value && <FaCheck />} {label}
                </button>
              ))}
            </div>
          </div>

          <div className="sf-filter-section sf-filter-section--last">
            <h3><FaShieldAlt /> {t('service_filter.verified_only')}</h3>
            <label className="sf-filter-toggle">
              <span>{t('service_filter.verified')}</span>
              <input type="checkbox" defaultChecked readOnly />
              <span className="sf-filter-toggle-track" />
            </label>
          </div>

          <div className="sf-filter-actions">
            <button type="submit" className="sf-filter-apply">
              {t('service_filter.apply')} {activeCount > 0 && `(${activeCount})`}
            </button>
            <button type="button" className="sf-filter-reset" onClick={handleReset}>
              <FaRedo /> {t('service_filter.reset')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(filterUI, document.body);
};

export default ServiceProviderFilter;